/**
 * 要注意ロジック（#3262）の判定本体。
 *
 * OCR結果（result_data）と読取パターンの項目設定（item_list）から、
 * 「人が見た方がよい項目」を判定して ReviewIssue の配列を返す。
 *
 * 判定材料は3系統。
 *   ① バリデーション: 項目設定の制約（必須・文字数・数値範囲・数値型・計算エラー）
 *   ② OCR読み取り信頼度: is_uncertain（Azure confidence が閾値未満）
 *   ③ 照合スコア: マスタ照合・辞書照合の score が閾値未満、または部分一致・候補なし
 *
 * フロント（保存時の再判定）と Functions（OCR完了時の判定）の双方から呼ぶため、
 * 外部状態に依存しない純関数として実装している。
 * Functions 側には functions/src/utils/pattern/reviewIssues.ts にミラーを置く
 * （applyIsUncertain・閾値定数と同じ二重管理の運用に揃えている）。
 */

import {
  MASTER_MATCH_REVIEW_THRESHOLD,
  reviewIssueCode,
  reviewIssueMessage,
  reviewIssueSource,
} from '../../constants/enum';
import { itemDataType, itemValidationResultMessage } from '../../constants/itemValidation';
import {
  checkDateRange,
  dateRangeMessage,
  matchesCharType,
  matchesPattern,
  type DateRangeRule,
} from './itemValidationRules';
import type {
  ItemChoiceType,
  ItemListChildType,
  ItemListType,
  ItemValidationType,
} from '../../types/common';
import type { ReviewIssue, ReviewSummary } from '../../types/reviewIssue';

/** result_data の1フィールド。判定に使う可能性のあるキーだけを型として持つ */
interface ResultField {
  value?: unknown;
  is_uncertain?: boolean;
  score?: unknown;
  /** 表示用の照合理由。実値を含む合成文字列のため判定には使わない（#3309） */
  match_reason_label?: unknown;
  /** 照合理由コード（#3309）。判定はこちらを優先して使う */
  match_reason?: unknown;
  calculation_error?: unknown;
}

/**
 * 照合理由コードごとの判定（#3309）。
 *
 * 'score' はスコアが閾値未満なら要注意にする。
 */
const MATCH_REASON_VERDICT: Record<string, 'ok' | 'score' | 'partial' | 'none'> = {
  confirmed: 'ok', // 照合履歴に完全一致
  exact_master: 'ok', // マスタと完全一致
  similar_confirmed: 'score', // 照合履歴に類似
  candidate: 'score', // マスタに類似
  partial_match: 'partial', // 部分一致
  no_match: 'none', // 照合したが候補なし
};

/**
 * 照合ラベルのうち、スコアを見るまでもなく判定が決まるもの。
 *
 * #3309 以降は match_reason コードで判定するが、それ以前に OCR した
 * 既存データにはコードが無いため、フォールバックとして残す。
 * ラベルは「マスタと完全一致（照合結果：〜）」のような合成文字列で、
 * 末尾にマスタの実値が入る。実値に「部分一致」等の語が含まれると
 * 誤判定するため、コードがあるときはそちらを必ず優先すること。
 */
const MATCH_LABEL_EXACT = ['照合履歴に完全一致', 'マスタと完全一致'];
const MATCH_LABEL_PARTIAL = '部分一致';
/**
 * 辞書変換のラベルに含まれる語（#3262）。
 *
 * ⚠️ 照合設定（dictionarySearchItemIds）が取れなかったときだけのフォールバック。
 * ラベルはマスタの実値を末尾に含む合成文字列のため、実値に「辞書」が入ると
 * マスタ由来を辞書由来と誤判定する（#3340）。項目IDで判別できるなら必ずそちらを使う。
 */
const DICTIONARY_LABEL_KEYWORD = '辞書';

/**
 * 照合スコア（"85%" 形式の文字列）を数値に変換する。
 *
 * マスタ照合・辞書照合は confidence を `${n}%` の文字列で保存しているため、
 * 閾値判定の前に数値化する必要がある。
 *
 * @returns 0〜100 の数値。解釈できない場合は null
 */
export const parseScore = (score: unknown): number | null => {
  if (typeof score === 'number') {
    return Number.isFinite(score) ? score : null;
  }
  if (typeof score !== 'string') return null;

  const trimmed = score.trim().replace(/%$/, '');
  if (trimmed === '') return null;

  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * 値が空かどうか。
 *
 * treatZeroAsEmpty が true の項目では 0 も空として扱う（#3262）。
 * あらたさんの発注書のように「0 は読み取れていない」とみなす帳票向けの設定で、
 * 金額0円・数量0が正当にありうる帳票では false（デフォルト）にする。
 */
const isEmptyValue = (value: unknown, treatZeroAsEmpty: boolean): boolean => {
  if (value === null || value === undefined) return true;

  const str = String(value).trim();
  if (str === '') return true;

  if (treatZeroAsEmpty) {
    const num = Number(str.replace(/,/g, ''));
    if (Number.isFinite(num) && num === 0) return true;
  }

  return false;
};

/** 数値として解釈できるか（カンマ区切りは許容する） */
const isNumericValue = (value: unknown): boolean => {
  const stripped = String(value).replace(/,/g, '').trim();
  if (stripped === '') return true; // 空値は必須チェックに委ねる
  return Number.isFinite(Number(stripped));
};

/**
 * 選択肢の出力値から表示名を引く（#3357）。
 * 見つからなければ値をそのまま返す（選択肢が変更された既存データ向け）。
 */
export const findChoiceLabel = (choices: ItemChoiceType[] | undefined, value: string): string => {
  const choice = choices?.find(c => (c.value || c.label) === value);
  return choice?.label ?? value;
};

/** ReviewIssue を1件組み立てる。message は「項目名 + 理由」の形に揃える */
const buildIssue = (params: {
  path: string;
  itemId: string;
  rowIndex?: number;
  label: string;
  source: ReviewIssue['source'];
  code: ReviewIssue['code'];
  reason: string;
}): ReviewIssue => ({
  path: params.path,
  item_id: params.itemId,
  ...(params.rowIndex !== undefined ? { row_index: params.rowIndex } : {}),
  label: params.label,
  source: params.source,
  code: params.code,
  // 項目名は label に持つため、message は理由のみにする。
  // 表示側で項目名が要る箇所（サマリー）だけ label と組み合わせる
  message: params.reason,
});

/**
 * ① バリデーション由来の判定。
 *
 * 項目設定の制約（#2815 / #3145）をそのまま判定材料に使う。
 * 正規表現制約は今期スコープ外のため、ここでは扱わない
 * （追加された際は下の判定を1つ足すだけで済む）。
 */
const evaluateValidation = (
  field: ResultField,
  item: ItemValidationType & { type?: string; choices?: ItemChoiceType[] },
  ctx: { path: string; itemId: string; rowIndex?: number; label: string },
  /** 日付範囲（#3357）の基準日。結果の created_at（OCR を実行した日）を渡す */
  dateRangeBaseDate?: Date,
): ReviewIssue[] => {
  const issues: ReviewIssue[] = [];
  const source = reviewIssueSource.validation;
  const value = field.value;

  // 計算エラー（#3145）。値の中身によらず、計算自体が失敗している
  if (field.calculation_error !== undefined && field.calculation_error !== null) {
    issues.push({
      ...buildIssue({
        ...ctx,
        source,
        code: reviewIssueCode.calculationError,
        reason: reviewIssueMessage.calculationError,
      }),
    });
    return issues; // 計算に失敗している以上、値の制約は評価しない
  }

  const treatZeroAsEmpty = item.treatZeroAsEmpty === true;

  // 必須チェック
  if (isEmptyValue(value, treatZeroAsEmpty)) {
    if (item.required === true) {
      issues.push(
        buildIssue({
          ...ctx,
          source,
          code: reviewIssueCode.required,
          reason: reviewIssueMessage.required,
        }),
      );
    }
    // 空値のときは required 以外の制約を評価しない
    return issues;
  }

  const strValue = String(value);

  // 数値型なのに数値として解釈できない
  if (item.type === itemDataType.number && !isNumericValue(value)) {
    issues.push(
      buildIssue({
        ...ctx,
        source,
        code: reviewIssueCode.notNumeric,
        reason: reviewIssueMessage.notNumeric,
      }),
    );
    return issues; // 数値でない以上、数値範囲は評価できない
  }

  // 文字数制約（string / textarea のみ。既存の supportsLengthConstraint と揃える）
  if (item.type === itemDataType.string || item.type === itemDataType.textarea) {
    if (item.minLength !== undefined && strValue.length < item.minLength) {
      issues.push(
        buildIssue({
          ...ctx,
          source,
          code: reviewIssueCode.minLength,
          reason: reviewIssueMessage.minLength(item.minLength),
        }),
      );
    }
    if (item.maxLength !== undefined && strValue.length > item.maxLength) {
      issues.push(
        buildIssue({
          ...ctx,
          source,
          code: reviewIssueCode.maxLength,
          reason: reviewIssueMessage.maxLength(item.maxLength),
        }),
      );
    }
  }

  // 数値範囲制約（number のみ）
  if (item.type === itemDataType.number) {
    const num = Number(strValue.replace(/,/g, ''));
    if (Number.isFinite(num)) {
      if (item.minValue !== undefined && num < item.minValue) {
        issues.push(
          buildIssue({
            ...ctx,
            source,
            code: reviewIssueCode.minValue,
            reason: reviewIssueMessage.minValue(item.minValue),
          }),
        );
      }
      if (item.maxValue !== undefined && num > item.maxValue) {
        issues.push(
          buildIssue({
            ...ctx,
            source,
            code: reviewIssueCode.maxValue,
            reason: reviewIssueMessage.maxValue(item.maxValue),
          }),
        );
      }
    }
  }

  // 文字種・入力パターン（#3357）。テキスト系のみ
  if (item.type === itemDataType.string || item.type === itemDataType.textarea) {
    if (item.charType !== undefined && !matchesCharType(strValue, item.charType)) {
      issues.push(
        buildIssue({
          ...ctx,
          source,
          code: reviewIssueCode.charType,
          reason: itemValidationResultMessage.charType(item.charType),
        }),
      );
    }
    if (!matchesPattern(strValue, item.pattern)) {
      issues.push(
        buildIssue({
          ...ctx,
          source,
          code: reviewIssueCode.pattern,
          // 設定者がエラー文言を書いていればそちらを理由にする
          reason:
            item.patternMessage !== undefined && item.patternMessage !== ''
              ? item.patternMessage
              : itemValidationResultMessage.pattern,
        }),
      );
    }
  }

  // 日付範囲（#3357）。基準日は OCR を実行した日（結果の created_at）。
  // 渡されていない場合は判定しない（判定が閲覧日基準にぶれるのを防ぐ）
  if (item.type === itemDataType.date && dateRangeBaseDate !== undefined) {
    const dateRule: DateRangeRule = {
      noPast: item.dateNoPast,
      noFuture: item.dateNoFuture,
      pastLimit: item.datePastLimit,
      pastUnit: item.datePastUnit,
      futureLimit: item.dateFutureLimit,
      futureUnit: item.dateFutureUnit,
    };
    const violation = checkDateRange(strValue, dateRule, dateRangeBaseDate);
    if (violation !== null) {
      issues.push(
        buildIssue({
          ...ctx,
          source,
          code: reviewIssueCode.dateRange,
          reason: dateRangeMessage(violation, dateRule),
        }),
      );
    }
  }

  // 要注意にする選択肢（#3357）。指定された値が選ばれていたら要注意にする。
  // 読み取った選択肢のチェック（「不明」なら人が確認）と、
  // 計算フィールドの判定結果（「不一致」なら要注意）の両方で使う
  if (item.reviewChoiceValues !== undefined && item.reviewChoiceValues.includes(strValue)) {
    issues.push(
      buildIssue({
        ...ctx,
        source,
        code: reviewIssueCode.reviewChoice,
        // 出力値ではなく表示名を出す。CSV 用のコード値（"9"）では人が読めないため
        reason: reviewIssueMessage.reviewChoice(findChoiceLabel(item.choices, strValue)),
      }),
    );
  }

  return issues;
};

/**
 * ② 読み取り信頼度由来の判定。
 *
 * applyIsUncertain が付与した is_uncertain をそのまま使う。
 * デフォルト ON。項目設定で reviewLowConfidence を false にした項目だけ出さない（#3384）。
 * Azure の読み取り精度が十分な帳票で、確認の手間を減らすための設定。
 */
const evaluateConfidence = (
  field: ResultField,
  item: ItemValidationType,
  ctx: { path: string; itemId: string; rowIndex?: number; label: string },
): ReviewIssue[] => {
  if (item.reviewLowConfidence === false) return [];
  if (field.is_uncertain !== true) return [];

  return [
    buildIssue({
      ...ctx,
      source: reviewIssueSource.confidence,
      code: reviewIssueCode.lowConfidence,
      reason: reviewIssueMessage.lowConfidence,
    }),
  ];
};

/**
 * ③ 照合スコア由来の判定。
 *
 * マスタ照合・辞書照合をしている項目のみが対象。
 * 照合していない項目（match_reason も match_reason_label も score も無い）は判定しない。
 *
 * 判定は match_reason コードを優先する（#3309）。コードが無い既存データは
 * ラベルの文字列判定にフォールバックする。
 */
const evaluateMatching = (
  field: ResultField,
  ctx: { path: string; itemId: string; rowIndex?: number; label: string },
  searchItemIds: Set<string> | undefined,
  reviewThresholds: Map<string, number> | undefined,
  dictionarySearchItemIds: Set<string> | undefined,
): ReviewIssue[] => {
  // 照合キー以外（column_mappings で値が反映された項目）は対象外。
  // 照合スコアは照合キーに紐づく情報で、反映先が10列あれば同じ内容が10箇所に出てしまう。
  // searchItemIds が未指定のときは絞り込まない
  if (searchItemIds !== undefined && !searchItemIds.has(ctx.itemId)) return [];

  // 要注意にする下限スコア。マスタ照合はマッピングごとに設定できる（#3309）。
  // 設定が無い項目・辞書変換は従来どおり全体の既定値を使う
  const threshold = reviewThresholds?.get(ctx.itemId) ?? MASTER_MATCH_REVIEW_THRESHOLD;

  const label = typeof field.match_reason_label === 'string' ? field.match_reason_label : '';
  const reason = typeof field.match_reason === 'string' ? field.match_reason : '';
  const score = parseScore(field.score);

  // 照合していない項目は対象外
  if (reason === '' && label === '' && score === null) return [];

  const source = reviewIssueSource.matching;
  // 辞書変換とマスタ照合は別機能なので文言を分ける。
  // 照合設定が取れていれば項目IDで判別する（#3340）。
  // match_reason コードは confirmed / candidate / no_match をマスタと辞書で共有しており、
  // コードだけではどちらの機能由来か区別できないため、設定側の情報が要る。
  const isDictionary =
    dictionarySearchItemIds !== undefined
      ? dictionarySearchItemIds.has(ctx.itemId)
      : // 設定が取れなかったときだけラベルの文字列で判別する。
        // マスタの実値に「辞書」が入ると誤判定するが、判別材料が他に無い
        label.includes(DICTIONARY_LABEL_KEYWORD);

  const partialIssue = (): ReviewIssue[] => [
    buildIssue({
      ...ctx,
      source,
      code: reviewIssueCode.partialMatch,
      reason: reviewIssueMessage.partialMatch,
    }),
  ];

  const noMatchIssue = (): ReviewIssue[] => [
    buildIssue({
      ...ctx,
      source,
      code: isDictionary ? reviewIssueCode.noDictionaryMatch : reviewIssueCode.noMatch,
      reason: isDictionary ? reviewIssueMessage.noDictionaryMatch : reviewIssueMessage.noMatch,
    }),
  ];

  // スコアが閾値未満なら要注意。スコアが取れない場合は判定できないため見送る
  const scoreIssue = (): ReviewIssue[] => {
    if (score === null || score >= threshold) return [];
    return [
      buildIssue({
        ...ctx,
        source,
        code: isDictionary ? reviewIssueCode.lowDictionaryScore : reviewIssueCode.lowMatchScore,
        reason: isDictionary
          ? reviewIssueMessage.lowDictionaryScore(score)
          : reviewIssueMessage.lowMatchScore(score),
      }),
    ];
  };

  // 部分一致は閾値未満のときだけ要注意にする（#3308 / #3309）。
  // 以前は confidence が 0 固定でスコアで測れなかったため常に要注意にしていたが、
  // #3308 で実スコアが付いた（ILIKE ヒット=100 / trigram のみ=similarity）。
  // 常に要注意のままだとそのスコアを無視することになる
  const partialMatchIssue = (): ReviewIssue[] => {
    if (score === null) return partialIssue(); // スコアが無い既存データは従来どおり
    if (score >= threshold) return [];
    return partialIssue();
  };

  // 照合理由コードがあればそれで判定する（#3309）
  const verdict = MATCH_REASON_VERDICT[reason];
  if (verdict !== undefined) {
    if (verdict === 'ok') return [];
    if (verdict === 'partial') return partialMatchIssue();
    if (verdict === 'none') return noMatchIssue();
    return scoreIssue();
  }

  // 以下は match_reason を保存する前（#3309 以前）に OCR した既存データ向けの
  // フォールバック。ラベルにマスタの実値が含まれるため誤判定しうる
  if (label.includes(MATCH_LABEL_PARTIAL)) return partialMatchIssue();
  if (MATCH_LABEL_EXACT.some(exact => label.includes(exact))) return [];

  return scoreIssue();
};

/**
 * 1フィールドについて3系統を評価する。
 *
 * onlySources を指定すると、その系統だけを評価する（#3309）。
 * 保存時の再判定でバリデーション由来だけを作り直すために使う。
 */
const evaluateField = (
  field: ResultField,
  item: ItemValidationType & { type?: string; choices?: ItemChoiceType[] },
  ctx: { path: string; itemId: string; rowIndex?: number; label: string },
  searchItemIds: Set<string> | undefined,
  onlySources?: Set<ReviewIssue['source']>,
  reviewThresholds?: Map<string, number>,
  dictionarySearchItemIds?: Set<string>,
  /** 日付範囲（#3357）の基準日。結果の created_at（OCR を実行した日）を渡す */
  dateRangeBaseDate?: Date,
): ReviewIssue[] => {
  const includes = (source: ReviewIssue['source']): boolean =>
    onlySources === undefined || onlySources.has(source);

  return [
    ...(includes(reviewIssueSource.validation)
      ? evaluateValidation(field, item, ctx, dateRangeBaseDate)
      : []),
    ...(includes(reviewIssueSource.confidence) ? evaluateConfidence(field, item, ctx) : []),
    ...(includes(reviewIssueSource.matching)
      ? evaluateMatching(field, ctx, searchItemIds, reviewThresholds, dictionarySearchItemIds)
      : []),
  ];
};

/** result_data の値を ResultField として解釈する。オブジェクトでなければ null */
const asResultField = (raw: unknown): ResultField | null => {
  if (raw === null || raw === undefined) return null;
  if (typeof raw !== 'object' || Array.isArray(raw)) return null;
  return raw as ResultField;
};

/** 有効な項目のみを対象にする（論理削除された項目は判定しない） */
const isActiveItem = (item: { is_active?: boolean }): boolean => item.is_active !== false;

/**
 * 要注意を判定する（#3262）。
 *
 * マスタ照合・辞書照合・固定値・計算フィールドがすべて適用された**後**の
 * result_data を渡すこと。適用前に判定すると、照合で入った値やスコアを
 * 判定材料に使えない（設計書 §4.5）。
 *
 * @param resultData OCR結果（result_data）
 * @param itemList 読取パターンの項目設定
 * @returns 要注意の判定一覧（発生順）
 */
export const evaluateReviewIssues = (
  resultData: Record<string, unknown> | undefined | null,
  itemList: ItemListType[] | undefined | null,
  /**
   * マスタ照合・辞書照合の**照合キー**の項目ID。
   * 指定すると、照合スコア由来の判定をこの項目だけに絞る。
   * 未指定なら絞り込まない（照合設定が取れなかった場合のフォールバック）。
   */
  searchItemIds?: Set<string>,
  /**
   * 評価する系統を絞る（#3309）。未指定なら3系統すべてを評価する。
   * 保存時の再判定では確信度の判定材料がフロントに揃わないため、
   * バリデーションと照合だけを指定して作り直す。
   */
  onlySources?: Set<ReviewIssue['source']>,
  /**
   * 照合キーの項目IDごとの、要注意にする下限スコア（#3309）。
   * master_mappings.review_threshold から作る。
   * 指定が無い項目は MASTER_MATCH_REVIEW_THRESHOLD を使う。
   */
  reviewThresholds?: Map<string, number>,
  /**
   * 辞書変換の**照合キー**の項目ID（#3340）。
   * 辞書由来かマスタ由来かを項目IDで判別し、要注意の文言を分けるために使う。
   * 未指定なら従来どおりラベルの文字列で判別する（照合設定が取れなかった場合のフォールバック）。
   */
  dictionarySearchItemIds?: Set<string>,
  /**
   * 日付範囲（#3357）の基準日。結果ドキュメントの created_at（OCR を実行した日）を渡す。
   * 閲覧日を使うと、保存済みの要注意と画面表示で判定が食い違うため呼び出し側から渡す。
   * 未指定なら日付範囲は判定しない
   */
  dateRangeBaseDate?: Date,
): ReviewIssue[] => {
  if (!resultData || !itemList) return [];

  const issues: ReviewIssue[] = [];

  for (const item of itemList) {
    if (!isActiveItem(item)) continue;

    const raw = resultData[item.id];

    // 明細（複数行データ）: result_data[親ID] が行の配列
    if (item.type === itemDataType.array) {
      const rows = Array.isArray(raw) ? raw : [];
      const activeChildren = (item.children ?? []).filter(isActiveItem);

      // 明細が1行も読み取れていない（#3309）。
      // 行ループは0行だと一度も回らないため、親項目の必須はここで判定する。
      // path は明細セル（親ID[行].子ID）ではなく親項目IDのみ
      if (
        item.required === true &&
        rows.length === 0 &&
        (onlySources === undefined || onlySources.has(reviewIssueSource.validation))
      ) {
        issues.push(
          buildIssue({
            path: item.id,
            itemId: item.id,
            label: item.parentValue,
            source: reviewIssueSource.validation,
            code: reviewIssueCode.required,
            reason: reviewIssueMessage.emptyDetail,
          }),
        );
      }

      rows.forEach((row, rowIndex) => {
        if (row === null || typeof row !== 'object') return;
        const rowObj = row as Record<string, unknown>;

        for (const child of activeChildren) {
          const field = asResultField(rowObj[child.id]);
          if (field === null) continue;

          issues.push(
            ...evaluateField(
              field,
              child as ItemListChildType,
              {
                path: `${item.id}[${rowIndex}].${child.id}`,
                itemId: child.id,
                rowIndex,
                // label は項目名のみ。行番号は row_index に持ち、
                // 行を跨いで並ぶサマリーでのみ表示側で組み立てる
                label: child.childValue,
              },
              searchItemIds,
              onlySources,
              reviewThresholds,
              dictionarySearchItemIds,
              dateRangeBaseDate,
            ),
          );
        }
      });
      continue;
    }

    // 単一項目
    const field = asResultField(raw);
    if (field === null) continue;

    issues.push(
      ...evaluateField(
        field,
        item,
        {
          path: item.id,
          itemId: item.id,
          label: item.parentValue,
        },
        searchItemIds,
        onlySources,
        reviewThresholds,
        dictionarySearchItemIds,
        dateRangeBaseDate,
      ),
    );
  }

  return issues;
};

/**
 * 判定結果を結果ドキュメントに保存する形へ集約する（#3262）。
 *
 * 明細（ReviewIssue[]）は保存しない（#3328・設計書 §4.8）。
 * 詳細画面が result_data と項目設定から判定し直すため、
 * 1MB 制限を避けるための打ち切りも不要になった。
 * ここで作るのは一覧が使う needs_review と、将来の件数表示に備える件数のみ。
 */
export const buildReviewSummary = (issues: ReviewIssue[]): ReviewSummary => ({
  needs_review: issues.length > 0,
  // 同じ項目に複数の理由があっても 1 項目と数える
  review_item_count: new Set(issues.map(issue => issue.path)).size,
  review_issue_count: issues.length,
});

/**
 * 要注意のある項目パスの集合を返す（#3262）。
 *
 * OCR結果画面で「この項目に要注意バッジを出すか」を引くために使う。
 * 単一項目は "itemId"、明細セルは "親ID[行index].子ID" をキーにする。
 */
export const buildReviewIssuePathSet = (issues: ReviewIssue[]): Set<string> =>
  new Set(issues.map(issue => issue.path));
